import { useState } from 'react'
import { X, Zap, CreditCard, CheckCircle2 } from 'lucide-react'

interface PaymentPackModalProps {
  isOpen: boolean
  onClose: () => void
}

const PACKAGES = [
  {
    id: 'lite',
    name: 'Lite pack',
    credits: 200,
    price: 12,
    originalPrice: 15,
    perCredit: 0.06,
    gradient: 'from-gray-400 to-gray-600',
    iconColor: 'text-white',
  },
  {
    id: 'boost',
    name: 'Boost pack',
    credits: 560,
    price: 28,
    originalPrice: 35,
    perCredit: 0.05,
    gradient: 'from-yellow-400 to-yellow-600',
    iconColor: 'text-white',
  },
  {
    id: 'creator',
    name: 'Creator pack',
    credits: 1500,
    price: 68,
    originalPrice: 85,
    perCredit: 0.045,
    gradient: 'from-pink-300 via-purple-300 to-indigo-400',
    iconColor: 'text-white',
  },
  {
    id: 'ultimate',
    name: 'Ultimate pack',
    credits: 3200,
    price: 96,
    originalPrice: 120,
    perCredit: 0.03,
    gradient: 'from-blue-400 to-purple-600',
    iconColor: 'text-white',
  },
]

const MOCK_TRANSACTIONS = [
  { id: '1', title: 'Purchase: Boost pack', date: '2024-01-08 14:30', amount: 560, type: 'earned' },
  {
    id: '2',
    title: 'Video Generation: "Morning Routine"',
    date: '2024-01-08 15:15',
    amount: -20,
    type: 'spent',
  },
  {
    id: '3',
    title: 'Video Generation: "Tech Review"',
    date: '2024-01-07 09:20',
    amount: -35,
    type: 'spent',
  },
  { id: '4', title: 'Purchase: Lite pack', date: '2024-01-05 11:00', amount: 200, type: 'earned' },
  { id: '5', title: 'Avatar Generation', date: '2024-01-04 16:45', amount: -15, type: 'spent' },
  {
    id: '6',
    title: 'Video Generation: "Travel Vlog"',
    date: '2024-01-03 10:30',
    amount: -40,
    type: 'spent',
  },
  { id: '7', title: 'Welcome Bonus', date: '2024-01-01 09:00', amount: 50, type: 'earned' },
]

export default function PaymentPackModal({ isOpen, onClose }: PaymentPackModalProps) {
  const [selectedPackId, setSelectedPackId] = useState<string>(PACKAGES[0]?.id || 'lite')
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'alipay'>('card')
  const [activeTab, setActiveTab] = useState<'topup' | 'details'>('topup')
  const [filter, setFilter] = useState<'all' | 'spent' | 'earned'>('all')

  const filteredTransactions = MOCK_TRANSACTIONS.filter((t) => {
    if (filter === 'all') return true
    return t.type === filter
  })

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-[95vw] max-w-[900px] overflow-hidden rounded-3xl bg-white shadow-2xl transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-6 top-6 z-10 cursor-pointer p-2 text-gray-400 transition-colors hover:text-gray-600"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="p-8">
          {/* Header Tabs */}
          <div className="mb-8 flex justify-center">
            <div className="flex w-full max-w-md rounded-xl bg-gray-100 p-1">
              <button
                className={`flex-1 rounded-lg py-2.5 text-sm font-medium transition-all ${
                  activeTab === 'topup'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('topup')}
              >
                Top-up credits
              </button>
              <button
                className={`flex-1 rounded-lg py-2.5 text-sm font-medium transition-all ${
                  activeTab === 'details'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('details')}
              >
                My credits details
              </button>
            </div>
          </div>

          {activeTab === 'topup' && (
            <>
              {/* Payment Method */}
              <div className="mb-8">
                <h3 className="mb-4 text-base font-semibold text-gray-900">Payment Method:</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <button
                    onClick={() => setPaymentMethod('card')}
                    className={`relative flex items-center justify-center gap-3 rounded-xl border-2 py-4 transition-all ${
                      paymentMethod === 'card'
                        ? 'border-indigo-600 bg-white ring-1 ring-indigo-600'
                        : 'border-gray-100 bg-white hover:border-gray-200'
                    }`}
                  >
                    <CreditCard className="h-6 w-6 text-gray-900" />
                    <span className="font-semibold text-gray-900">Credit Card</span>
                    {paymentMethod === 'card' && (
                      <CheckCircle2 className="absolute bottom-3 right-3 h-5 w-5 fill-indigo-600 text-white" />
                    )}
                  </button>
                  <button
                    onClick={() => setPaymentMethod('alipay')}
                    className={`relative flex items-center justify-center gap-3 rounded-xl border-2 py-4 transition-all ${
                      paymentMethod === 'alipay'
                        ? 'border-indigo-600 bg-white ring-1 ring-indigo-600'
                        : 'border-gray-100 bg-white hover:border-gray-200'
                    }`}
                  >
                    {/* Placeholder for Alipay/Wechat icons */}
                    <div className="flex gap-2">
                      <div className="flex h-6 w-10 items-center justify-center rounded bg-blue-500 text-[10px] font-bold text-white">
                        支付宝
                      </div>
                      <div className="flex h-6 w-6 items-center justify-center rounded bg-green-500 text-[10px] font-bold text-white">
                        微信
                      </div>
                    </div>
                    <span className="font-semibold text-gray-900">AliPay / Wechat Pay</span>
                    {paymentMethod === 'alipay' && (
                      <CheckCircle2 className="absolute bottom-3 right-3 h-5 w-5 fill-indigo-600 text-white" />
                    )}
                  </button>
                </div>
              </div>

              {/* Select Pack */}
              <div className="mb-8">
                <h3 className="mb-4 text-base font-semibold text-gray-900">Select pack:</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {PACKAGES.map((pkg) => (
                    <div
                      key={pkg.id}
                      onClick={() => setSelectedPackId(pkg.id)}
                      className={`relative flex cursor-pointer flex-col items-center overflow-hidden rounded-2xl border-2 transition-all hover:shadow-lg ${
                        selectedPackId === pkg.id
                          ? 'border-indigo-600 bg-white ring-1 ring-indigo-600'
                          : 'border-gray-100 bg-white hover:border-gray-200'
                      }`}
                    >
                      {/* Gradient Header */}
                      <div
                        className={`mb-4 flex h-32 w-full items-center justify-center bg-gradient-to-br ${pkg.gradient}`}
                      >
                        <Zap className={`h-16 w-16 ${pkg.iconColor} drop-shadow-md`} />
                      </div>

                      <div className="flex w-full flex-col px-4 pb-6 text-center">
                        <h4 className="mb-2 text-lg font-bold text-gray-900">{pkg.name}</h4>

                        <div className="mb-1 text-3xl font-bold text-amber-400 drop-shadow-sm">
                          {pkg.credits}
                        </div>
                        <div className="mb-4 text-sm font-medium text-gray-500">Credits</div>

                        {/* Dashed Separator */}
                        <div className="mb-4 w-full border-t-2 border-dashed border-gray-100"></div>

                        <div className="flex flex-col items-center">
                          <span className="text-sm text-gray-400 line-through">
                            ${pkg.originalPrice}
                          </span>
                          <span className="text-3xl font-bold text-gray-900">${pkg.price}</span>
                          <span className="mt-1 text-xs text-gray-500">
                            $ {pkg.perCredit}/credit
                          </span>
                        </div>
                      </div>

                      {selectedPackId === pkg.id && (
                        <CheckCircle2 className="absolute bottom-3 right-3 h-5 w-5 fill-indigo-600 text-white" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Purchase Button */}
              <div className="flex justify-center">
                <button className="w-full max-w-md rounded-xl bg-gray-900 py-3.5 text-base font-bold text-white shadow-lg transition-all hover:bg-gray-800 hover:shadow-xl active:scale-[0.98]">
                  Purchase
                </button>
              </div>
            </>
          )}

          {activeTab === 'details' && (
            <div className="flex h-[500px] flex-col">
              {/* Filter Tabs */}
              <div className="mb-6 flex gap-2 border-b border-gray-100 pb-1">
                {(['all', 'spent', 'earned'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setFilter(f)}
                    className={`relative px-4 py-2 text-sm font-medium transition-colors ${
                      filter === f ? 'text-indigo-600' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                    {filter === f && (
                      <div className="absolute bottom-[-5px] left-0 h-0.5 w-full bg-indigo-600" />
                    )}
                  </button>
                ))}
              </div>

              {/* Transaction List */}
              <div className="flex-1 overflow-y-auto pr-2 scrollbar-thin scrollbar-track-gray-50 scrollbar-thumb-gray-200 hover:scrollbar-thumb-gray-300">
                <div className="flex flex-col gap-3">
                  {filteredTransactions.map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between rounded-xl border border-gray-100 bg-white p-4 transition-all hover:border-gray-200 hover:shadow-sm"
                    >
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold text-gray-900">{t.title}</span>
                        <span className="text-xs text-gray-400">{t.date}</span>
                      </div>
                      <div
                        className={`text-lg font-bold ${
                          t.amount > 0 ? 'text-green-600' : 'text-gray-900'
                        }`}
                      >
                        {t.amount > 0 ? '+' : ''}
                        {t.amount}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
